import React, { memo, useMemo, useRef, useState, useCallback, useEffect, useContext } from 'react';
import {
    Text,
    Popover,
    Spacer,
    SearchBar,
    Tabs,
    Tooltip,
    Icon,
    Link,
    useTheme,
} from '@Sage/hoi-poi-ui';
import { useSelector, useDispatch } from 'react-redux';
import { EntityModalActions } from 'actions';
import { getDashboards } from 'services/Dashboards';
import { ENTITY_MODAL_UPDATE } from 'lib/events';
import { subscribe } from 'lib/EventBuser';
import { getLiteral } from 'utils/getLiteral';
import { removeAccents } from 'utils/strings';
import { logEvent } from 'utils/tracking';
import AbsoluteError from 'components/AbsoluteError';
import AbsoluteEmpty from 'components/AbsoluteEmpty';
import MenuHoi from 'components/MenuHoi';
import { ANALYTICS_REPORT, DASHBOARD } from 'constants/Entities';
import { FORCEBI } from 'constants/Features';
import { DASHBOARD_TYPE, METABASE_REPORT } from 'constants/DashboardProviders';

import { DashboardContext } from './DashboardContext';

const DASHBOARD_TABS = [
    {
        label: 'label_all_dashboards',
        key: 'all',
        icon: 'apps',
    },
    {
        label: 'label_dashboards',
        key: 'dashboards',
        icon: 'chart',
    },
    {
        label: 'title_sales_force',
        key: 'sfm',
        icon: 'salesForce',
        permission: 'sfmPermission',
    },
];

const AddDashboardPopover = memo(({ placement, onAdd, children }) => {
    const theme = useTheme();
    const timer = useRef();
    const dispatch = useDispatch();
    const { dashboards } = useContext(DashboardContext);
    const [dashboardList, setDashboardList] = useState();
    const [recentlyDashboardList, setRecentlyDashboardList] = useState();
    const [searchText, setSearchText] = useState();
    const [activeTab, setActiveTab] = useState(DASHBOARD_TABS[0].key);
    const [error, setError] = useState(false);
    const [popoverVisible, setPopoverVisible] = useState(false);
    const permissions = useSelector((state) => ({
        sfmPermission: state.config?.permission?.sfm,
        analyticsPro: state.config?.permission?.analyticsPro,
        analyticsProCreateDashboard: state.config?.permission?.analyticsProCreateDashboard || false,
    }));
    const hasFORCEBI = useSelector((state) => state.config.flagsmith?.[FORCEBI] || false);

    const addButtonOptions = useMemo(() => {
        const options = [];

        // ForceBI dashboard option (only if hasFORCEBI and permissions)
        if (permissions.analyticsPro && permissions.analyticsProCreateDashboard && hasFORCEBI) {
            options.push({
                onClick: () => {
                    dispatch(
                        EntityModalActions.init({
                            entity: ANALYTICS_REPORT,
                            trackingSubmodule: 'forceBI',
                            data: { folderId: -1 }, // Root folder
                            labels: {
                                title: getLiteral('label_create_dashboard'),
                                success: getLiteral('succes_entitycreatedsuccessfully'),
                                error: getLiteral('label_failed_create'),
                            },
                        }),
                    );
                },
                title: getLiteral('label_create_dashboard'),
                description: getLiteral('label_analytics_pro_new_report_desc'),
                icon: 'newReport',
            });
        }

        // External dashboard option (always available if has permissions)
        if (permissions.analyticsPro && permissions.analyticsProCreateDashboard) {
            options.push({
                onClick: () => {
                    logEvent({
                        event: DASHBOARD.trueName,
                        submodule: 'powerBI',
                        functionality: 'create',
                    });
                    dispatch(
                        EntityModalActions.init({
                            entity: ANALYTICS_REPORT,
                            trackingSubmodule: 'externalDashboard',
                            data: {
                                folderId: -1, // Root folder
                                provider: 5,
                            },
                            labels: {
                                title: getLiteral('label_new_external_dashboard'),
                                success: getLiteral('succes_entitycreatedsuccessfully'),
                                error: getLiteral('label_failed_create'),
                            },
                        }),
                    );
                },
                title: getLiteral('label_new_external_dashboard'),
                description: getLiteral('label_new_external_dashboard_desc'),
                icon: 'newReport',
            });
        }

        return options;
    }, [permissions.analyticsPro, permissions.analyticsProCreateDashboard, hasFORCEBI, dispatch]);

    const getDashboardsList = useCallback(
        (createdId) => {
            return getDashboards()
                .then((result) => {
                    setRecentlyDashboardList(
                        result
                            .filter(
                                (dashboard) =>
                                    dashboard.dateUsed &&
                                    dashboard?.type?.value !== DASHBOARD_TYPE.SFM,
                            )
                            ?.filter((dashboard) => {
                                // Exclude current dashboard tabs
                                return !dashboards.find((d) => d.id === dashboard.id);
                            })
                            ?.filter(
                                (dashboard) =>
                                    !(
                                        parseInt(dashboard?.type?.id, 10) ===
                                            parseInt(METABASE_REPORT, 10) &&
                                        dashboard?.visibleIn?.length > 0
                                    ),
                            )
                            .map((dashboard) => {
                                if (dashboard?.type?.value === DASHBOARD_TYPE.SFM)
                                    dashboard.title = getLiteral(dashboard.title);
                                return dashboard;
                            })
                            .sort((a, b) => new Date(b.dateUsed) - new Date(a.dateUsed))
                            .slice(0, 4),
                    );
                    setDashboardList(
                        result
                            .map((dashboard) => {
                                if (dashboard?.type?.value === DASHBOARD_TYPE.SFM)
                                    dashboard.title = getLiteral(dashboard.title);
                                return dashboard;
                            })
                            .sort((a, b) => a?.title?.localeCompare(b?.title)),
                    );
                    if (createdId) {
                        return result.reduce((obj, dashboard) => {
                            if (dashboard.id === createdId) obj = dashboard;
                            return obj;
                        }, {});
                    }
                    return null;
                })
                .catch((e) => {
                    console.error(e);
                    setError(true);
                });
        },
        [dashboards],
    );

    useEffect(() => {
        setError(false);
        getDashboardsList();
    }, [getDashboardsList]);

    const onChangeSearch = useCallback((event) => {
        const text = event?.target?.value || '';
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            setSearchText(text);
        }, 200);
    }, []);

    const getRow = useCallback(
        (dashboard, index) => {
            return (
                <div
                    key={index}
                    className="fm-dashboards-tabs__add-popover__row"
                    onClick={() => onAdd(dashboard)}
                >
                    <div className="fm-dashboards-tabs__add-popover__row-title">
                        <Icon
                            name={
                                dashboard?.type?.value === DASHBOARD_TYPE.SFM
                                    ? 'salesForce'
                                    : 'chart'
                            }
                            color={theme.colors.grey[500]}
                        />
                        <Text isTruncated useTooltip color={theme.colors.utility.textPrimary}>
                            {dashboard.title}
                        </Text>
                    </div>
                    {dashboard?.owner && (
                        <Text
                            className="fm-dashboards-tabs__add-popover__row-owner"
                            type="caption"
                            color={theme.colors.utility.textSecondary}
                            isTruncated
                            useTooltip
                        >
                            {dashboard?.owner?.value}
                        </Text>
                    )}
                </div>
            );
        },
        [onAdd, theme],
    );

    const list = useMemo(() => {
        if (error) return null;
        const cleanText = removeAccents(searchText);
        return dashboardList
            ?.filter((dashboard) => {
                return !recentlyDashboardList.find((d) => d.id === dashboard.id);
            })
            ?.filter((dashboard) => {
                // Filtering by tab type
                switch (activeTab) {
                    case 'dashboards':
                        return [
                            DASHBOARD_TYPE.FORCE_BI,
                            DASHBOARD_TYPE.METABASE,
                            DASHBOARD_TYPE.POWER_BI,
                        ].includes(dashboard?.type?.value);
                    case 'sfm':
                        return DASHBOARD_TYPE.SFM === dashboard?.type?.value;
                    case 'all':
                    default:
                        return true;
                }
            })
            ?.filter((dashboard) => {
                // Exclude current dashboard tabs
                return !dashboards.find((d) => d.id === dashboard.id);
            })
            ?.filter((dashboard) => {
                // Filtering by search text
                const cleanTitle = removeAccents((dashboard?.title || '').toLowerCase());
                return cleanTitle.includes(cleanText);
            })
            .map(getRow);
    }, [activeTab, dashboardList, dashboards, error, getRow, recentlyDashboardList, searchText]);

    const onTogglePopover = useCallback(() => {
        setPopoverVisible(!popoverVisible);
    }, [popoverVisible]);

    useEffect(() => {
        return subscribe(`${ENTITY_MODAL_UPDATE}--${ANALYTICS_REPORT.entity}`, ({ result }) => {
            if (result.code) {
                const dashboardId = result.code;
                getDashboardsList(dashboardId).then((dashboard) => {
                    if (dashboard?.id) onAdd(dashboard);
                });
            }
        });
    }, [getDashboardsList, onAdd]);

    const trackAddPopover = useCallback(() => {
        logEvent({
            event: 'dashboard',
            functionality: 'addTab',
        });
    }, []);

    const visibleOverride = useMemo(
        () => ({
            root: { visible: popoverVisible, onVisibleChange: onTogglePopover },
        }),
        [popoverVisible, onTogglePopover],
    );

    const content = useMemo(() => {
        const showEmpty = !list?.length && !error;

        return (
            <div className="fm-dashboards-tabs__add-popover__content">
                <div className="fm-dashboards-tabs__add-popover__header">
                    <div className="fm-dashboards-tabs__add-popover__title">
                        <Text type="subtitle" medium>
                            {getLiteral('title_add_tab_dashboard')}
                        </Text>
                        {permissions.analyticsPro &&
                            permissions.analyticsProCreateDashboard &&
                            addButtonOptions.length > 0 && (
                                <div className="fm-dashboards-tabs__add-popover__button">
                                    <MenuHoi items={addButtonOptions}>
                                        <div className="fm-dashboards-tabs__add-popover__button-content">
                                            <Icon
                                                name="addCircle"
                                                color={theme.colors.actionMajor[500]}
                                            />
                                            <Link variation="primary" type="button">
                                                {getLiteral('label_create_dashboard')}
                                            </Link>
                                        </div>
                                    </MenuHoi>
                                </div>
                            )}
                    </div>
                    <Spacer y={2} />
                    <SearchBar
                        placeholder={getLiteral('placerholder_search_dashboards')}
                        useAsSimpleSearch={true}
                        onChange={onChangeSearch}
                    />
                </div>
                <div className="fm-dashboards-tabs__add-popover__tabs">
                    <Tabs
                        onChange={setActiveTab}
                        activeKey={activeTab}
                        tabs={DASHBOARD_TABS?.filter(
                            (tab) => !tab.permission || permissions[tab.permission],
                        ).map((tab) => ({
                            key: tab.key,
                            title: (
                                <Tooltip
                                    key={tab.key}
                                    placement="top"
                                    content={getLiteral(tab.label)}
                                >
                                    <span>
                                        <Icon
                                            className="fm-dashboards-tabs__add-popover__tabs__icon"
                                            name={tab.icon}
                                            size="large"
                                            color={
                                                activeTab === tab.key
                                                    ? theme.colors.textLight.primary
                                                    : theme.colors.grey[500]
                                            }
                                        />
                                    </span>
                                </Tooltip>
                            ),
                        }))}
                    />
                </div>
                <div className="fm-dashboards-tabs__add-popover__list">
                    {activeTab !== 'sfm' && (
                        <>
                            {!!recentlyDashboardList?.length && !!recentlyDashboardList?.length && (
                                <>
                                    <Text type="subtitle" medium>
                                        {getLiteral('title_recently_viewed')}
                                    </Text>
                                    <Spacer y={1} />
                                </>
                            )}
                            {recentlyDashboardList?.map(getRow)}
                            {!!recentlyDashboardList?.length && !!dashboardList?.length && (
                                <>
                                    <Spacer y={1} />
                                    <Text type="subtitle" medium>
                                        {getLiteral('label_all_dashboards')}
                                    </Text>
                                    <Spacer y={1} />
                                </>
                            )}
                        </>
                    )}
                    {list}
                    {showEmpty && (
                        <AbsoluteEmpty
                            title={getLiteral('label_empty_screen_add_dashboards')}
                            subtitle={getLiteral('label_empty_screen_add_dashboard_desc')}
                            size="popover"
                        />
                    )}
                    {error && <AbsoluteError size="popover" />}
                </div>
            </div>
        );
    }, [
        list,
        error,
        permissions,
        addButtonOptions,
        theme.colors.actionMajor,
        theme.colors.textLight.primary,
        theme.colors.grey,
        onChangeSearch,
        activeTab,
        recentlyDashboardList,
        getRow,
        dashboardList?.length,
    ]);

    return (
        <div onClick={trackAddPopover}>
            <Popover
                className="fm-dashboards-tabs__add-popover"
                placement={placement}
                content={content}
                overrides={visibleOverride}
            >
                {children}
            </Popover>
        </div>
    );
});

export default AddDashboardPopover;
